import {
  Alert,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
  TextInput,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import {isEmpty, isNil, isNotNil, not} from 'ramda';

import {useGetSeriesListQuery, useLazyGetBranchQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {GetBranchApiResponse} from '@dms/api/shared';
import {
  GetTransferOrderVariantResponse,
  usePatchTransferOrderVariantMutation,
  usePostTransferOrderVariantMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {getOptionsFromSeriesList, SettingsFooter} from '@dms/settings/shared';
import {
  getOptionsFromAuthorizationProfiles,
  handleApiError,
  useDuplicateErrorHandler,
  useGetCurrentBranch,
  useTenant,
} from '@dms/shared';

import {ApiError, Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {
  TransferOrderVariantForm,
  TransferOrderVariantFormSchema,
} from '../types/TransferOrderVariantForm';

interface TransferOrderVariantsDetailFormProps extends RequiredTestIdProps {
  isDraft: boolean;
  transferOrderVariant: GetTransferOrderVariantResponse | Nullish;
}

export function TransferOrderVariantsDetailForm(props: TransferOrderVariantsDetailFormProps) {
  const {id} = useParams();

  const navigate = useNavigate();

  const {duplicateError: isDuplicated, duplicateErrorHandler} = useDuplicateErrorHandler();

  const [branch, setBranch] = useState<GetBranchApiResponse | Nullish>(null);

  const {tenant} = useTenant();

  const {activeBranchId} = useGetCurrentBranch();

  const authorizationProfilesQuery = useGetAuthorizationProfilesQuery(
    {
      'x-tenant': tenant?.id as string,
      branchId: props.transferOrderVariant?.branchId ?? activeBranchId,
    },
    {skip: isNil(tenant?.id) || isEmpty(tenant?.id)}
  );

  const documentSeriesQuery = useGetSeriesListQuery({
    type: ['warehouse/transfer-order'],
  });

  const [getBranch] = useLazyGetBranchQuery();
  const [postTransferOrderVariant] = usePostTransferOrderVariantMutation();
  const [patchTransferOrderVariant] = usePatchTransferOrderVariantMutation();

  useEffect(
    function loadTransferOrderBranch() {
      if (isNil(props.transferOrderVariant?.branchId) || isNil(activeBranchId)) {
        return;
      }
      getBranch({branchId: props.transferOrderVariant?.branchId ?? activeBranchId})
        .unwrap()
        .then((branch) => setBranch(branch))
        .catch(handleApiError);
    },
    [props.transferOrderVariant?.branchId, activeBranchId, getBranch]
  );

  const handleNavigateBack = () => {
    navigate(settingsRoutes.warehousesTransferOrderVariants);
  };

  const handleSubmit: FormSubmitHandler<TransferOrderVariantForm> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (not(props.isDraft) && isNotNil(id)) {
      return await patchTransferOrderVariant({
        transferOrderVariantId: id,
        body: {
          name: formValues.name,
          authorizationProfileId: formValues.authorizationProfileId,
          docSeriesId: formValues.docSeriesId,
          printoutTemplateId: formValues.printoutTemplateId,
        },
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          handleNavigateBack();
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await postTransferOrderVariant({
      name: formValues.name,
      authorizationProfileId: formValues.authorizationProfileId,
      docSeriesId: formValues.docSeriesId,
      printoutTemplateId: formValues.printoutTemplateId,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch(duplicateErrorHandler);
  };

  const defaultValues: Partial<TransferOrderVariantForm> = {
    name: props.transferOrderVariant?.name,
    authorizationProfileId: props.transferOrderVariant?.authorizationProfileId,
    docSeriesId: props.transferOrderVariant?.docSeriesId,
    printoutTemplateId: props.transferOrderVariant?.printoutTemplateId,
  };

  return (
    <Form<TransferOrderVariantForm>
      experimentalZodSchema={TransferOrderVariantFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi, navigateWithoutBlocker) => {
        const handleAuthorizationProfileChange = (
          selectedAuthorizationProfileId: string | number | null
        ) => {
          if (isNil(selectedAuthorizationProfileId)) {
            return;
          }

          const branchId = authorizationProfilesQuery.data?.find(
            (authorizationProfile) => authorizationProfile.id === selectedAuthorizationProfileId
          )?.branchId;

          if (isNil(branchId)) {
            return;
          }

          getBranch({branchId})
            .unwrap()
            .then((branch) => setBranch(branch))
            .catch(handleApiError);
        };
        return (
          <VStack spacing={6} data-testid={props['data-testid']}>
            <Show when={isDuplicated}>
              <Alert
                variant="error"
                title={i18n.t('entity.warehouse.notifications.transferOrderVariantAlreadyExists')}
                data-testid={suffixTestId('duplicateAlert', props)}
              />
            </Show>
            <HStack width={200}>
              <Box width="100%">
                <FormField
                  isRequired
                  control={control}
                  type="text"
                  name="name"
                  label={i18n.t('entity.warehouse.labels.name')}
                  data-testid={suffixTestId('inputs.name', props)}
                />
              </Box>
            </HStack>
            <HStack width={200} spacing={4}>
              <Box width={98}>
                <FormField
                  isRequired
                  isNotClearable
                  isLoading={authorizationProfilesQuery.isLoading}
                  control={control}
                  type="choice"
                  name="authorizationProfileId"
                  label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                  options={getOptionsFromAuthorizationProfiles(
                    authorizationProfilesQuery.data,
                    formApi.watch('authorizationProfileId')
                  )}
                  onChange={handleAuthorizationProfileChange}
                  data-testid={suffixTestId('inputs.authorizationProfileId', props)}
                />
              </Box>
              <Box width={98}>
                <TextInput
                  isDisabled
                  label={i18n.t('entity.warehouse.labels.branch')}
                  value={branch?.name ?? ''}
                  data-testid={suffixTestId('inputs.branch', props)}
                />
              </Box>
            </HStack>
            <HStack width={200} spacing={4}>
              <Box width={98}>
                <FormField
                  isRequired
                  isNotClearable
                  isLoading={documentSeriesQuery.isLoading}
                  control={control}
                  type="choice"
                  name="docSeriesId"
                  label={i18n.t('entity.warehouse.labels.transferOrderDocSeries')}
                  options={getOptionsFromSeriesList(
                    documentSeriesQuery.data,
                    'warehouse/transfer-order'
                  )}
                  data-testid={suffixTestId('inputs.docSeriesId', props)}
                />
              </Box>
              <Box width={98}>
                <FormField
                  control={control}
                  type="choice"
                  name="printoutTemplateId"
                  label={i18n.t('entity.warehouse.labels.transferOrderTemplate')}
                  options={[]}
                  data-testid={suffixTestId('inputs.printoutTemplateId', props)}
                />
              </Box>
            </HStack>
            <SettingsFooter
              actions={[
                {
                  type: 'button',
                  variant: 'secondary',
                  title: props.isDraft
                    ? i18n.t('general.actions.discard')
                    : i18n.t('general.actions.discardChanges'),
                  onClick: () =>
                    navigateWithoutBlocker(settingsRoutes.warehousesTransferOrderVariants),
                  'data-testid': suffixTestId('actions.discard', props),
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  variant: 'primary',
                  title: props.isDraft
                    ? i18n.t('general.actions.create')
                    : i18n.t('general.actions.saveChanges'),
                  'data-testid': suffixTestId('actions.submit', props),
                },
              ]}
            />
          </VStack>
        );
      }}
    </Form>
  );
}
